import { useState } from "react";
import { defaultModel, Model } from "./Model";
import { CiudadResumida, Sector } from "../../../services/paisesServicios";
import { ServicioTransporte } from "../../../services/transporteServicios";
import { ItemListadoServiciosHotel } from "../../../services/hotelServicios";
import { RespuestaBuscarPropietario } from "../../../services/propietariosServicios";

// En este custom hook se definen mutaciones del modelo en casa paso del Wizard.
// Muchas mutaciones implican el reseteo de pasos posteriores.
//
// Por ejemplo: Si se cambia la mascota seleccionada, se deben resetear las fechas de
// la reserva porque la disponibilidad es distinta para cada mascota.
export const usePasoReservaHotel = (model: Model) => {
  const [currentModel, setCurrentModel] = useState<Model>(model);

  const seleccionarMascota = (datosAccion: {
    idMascota: string;
    nombreMascota: string;
    idPropietario: string;
    nombresPropietario: string;
  }) => {
    setCurrentModel((prevModel) => {
      // Reiniciando propiedades, las propidades de pasos futuros quedan indefinidas
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.SeleccionarMascota
      );

      const updatedModel: Model = {
        ...modelReseteado,
        idMascota: datosAccion.idMascota,
        nombreMascota: datosAccion.nombreMascota,
        idPropietario: datosAccion.idPropietario,
        nombresPropietario: datosAccion.nombresPropietario,
      };
      return updatedModel;
    });
  };

  const definirDatosPropietario = (datosAccion: {
      datosPropietario: RespuestaBuscarPropietario
    }) => {
      setCurrentModel((prevModel) => {
        // Reiniciando propiedades, las propidades de pasos futuros quedan indefinidas
        let modelReseteado = resetearValoresPasosPosteriores(
          prevModel,
          IndicesPasos.SeleccionarMascota
        );
  
        const updatedModel: Model = {
          ...modelReseteado,
          datosPropietario: datosAccion.datosPropietario,
        };
        return updatedModel;
      });
    };

  const definirNochesIndividuales = (datosAccion: {
    nochesIndividuales: number;
    valorFinalNocheIndividual: number;
    tarifaNocheIndividual?: ItemListadoServiciosHotel;
  }) => {
    setCurrentModel((prevModel) => {
      // Las propiedades de pasos futuros quedan indefinidas
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosBasicos
      );

      const updatedModel: Model = {
        ...modelReseteado,
        numeroNochesIndividuales: datosAccion.nochesIndividuales,
        valorFinalNocheIndividual: datosAccion.valorFinalNocheIndividual,
        tarifaNocheIndividual: datosAccion.tarifaNocheIndividual,
      };
      return updatedModel;
    });
  };

  const definirContrataTransporte = (datosAccion: {
    contrataTransporte: boolean;
  }) => {
    const contrata = datosAccion.contrataTransporte;

    setCurrentModel((prevModel) => {
      // Las propiedades de pasos futuros quedan indefinidas
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosBasicos
      );

      const updatedModel: Model = {
        ...modelReseteado,
        // Propiedades de este paso (Datos Basicos) y accion
        contrataTransporte: contrata,
        contrataIda: contrata,
        contrataVuelta: contrata,
      };
      return updatedModel;
    });
  };

  const definirCiudadOrigen = (datosAccion: {
    ciudadOrigen: CiudadResumida | undefined;
  }) => {
    setCurrentModel((prevModel) => {
      // Las propiedades de pasos futuros quedan indefinidas
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosBasicos
      );

      const updatedModel: Model = {
        ...modelReseteado,
        ciudadOrigen: datosAccion.ciudadOrigen
      };
      return updatedModel;
    });
  };

  const seleccionarPlan = (datosAccion: {
    plan: ItemListadoServiciosHotel;
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosBasicos
      );

      const updatedModel: Model = {
        ...modelReseteado,
        // Propiedades de este paso (Datos Basicos) y accion
        tarifa: datosAccion.plan,
        valorFinalTarifa: datosAccion.plan.precioNoche,
      };
      return updatedModel;
    });
  };

  const definirFechas = (datosAccion: { dias: Date[]; noches: string[] }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.Fechas
      );

      const updatedModel: Model = {
        ...modelReseteado,
        diasSeleccionados: datosAccion.dias,
        fechasProgramadas: datosAccion.noches,
      };
      return updatedModel;
    });
  };

  const definirDireccionTransporte = (datosAccion: {
    direccion: string | undefined;
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        direccionTransporte: datosAccion.direccion,
      };
      return updatedModel;
    });
  };

  const definirSectorTransporte = (datosAccion: {
    sector: Sector | undefined;
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        sectorTransporte: datosAccion.sector,
      };
      return updatedModel;
    });
  };

  const definirContrataIda = (datosAccion: { contrata: boolean }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        contrataIda: datosAccion.contrata,
      };
      return updatedModel;
    });
  };

  const definirContrataVuelta = (datosAccion: { contrata: boolean }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        contrataVuelta: datosAccion.contrata,
      };
      return updatedModel;
    });
  };

  const definirTarifaIda = (datosAccion: { tarifaIda: ServicioTransporte }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.CalculoPrecioTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        tarifaIda: datosAccion.tarifaIda,
        valorFinalIda: datosAccion.tarifaIda.precio,
      };
      return updatedModel;
    });
  };

  const definirTarifaVuelta = (datosAccion: {
    tarifaVuelta: ServicioTransporte;
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.CalculoPrecioTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        tarifaVuelta: datosAccion.tarifaVuelta,
        valorFinalVuelta: datosAccion.tarifaVuelta.precio,
      };
      return updatedModel;
    });
  };

  const definirTarifaIdaVuelta = (datosAccion: {
    tarifaIdaVuelta: ServicioTransporte;
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.CalculoPrecioTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        tarifaIdaVuelta: datosAccion.tarifaIdaVuelta,
        valorFinalIdaVuelta: datosAccion.tarifaIdaVuelta.precio,
      };
      return updatedModel;
    });
  };

  const reiniciarTarifasTransporte = () => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.CalculoPrecioTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        tarifaIda: undefined,
        valorFinalIda: undefined,
        tarifaVuelta: undefined,
        valorFinalVuelta: undefined,
        tarifaIdaVuelta: undefined,
        valorFinalIdaVuelta: undefined,
      };
      return updatedModel;
    });
  };

  const definirUsarTarifasPorTrayecto = (datosAccion: {
    usarTarifasPorTrayecto: boolean;
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.CalculoPrecioTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        usarTarifasPorTrayecto: datosAccion.usarTarifasPorTrayecto,
      };
      return updatedModel;
    });
  };

  return {
    currentModel,
    seleccionarMascota,
    definirDatosPropietario,
    definirNochesIndividuales,
    definirContrataTransporte,
    definirCiudadOrigen,
    seleccionarPlan,
    definirFechas,
    definirDireccionTransporte,
    definirSectorTransporte,
    definirContrataIda,
    definirContrataVuelta,
    definirTarifaIda,
    definirTarifaVuelta,
    definirTarifaIdaVuelta,
    reiniciarTarifasTransporte,
    definirUsarTarifasPorTrayecto,
  };
};

const resetearSeleccionarMascota = (model: Model): Model => {
  return {
    ...model,
    idMascota: defaultModel.idMascota,
    nombreMascota: defaultModel.nombreMascota,
    idPropietario: defaultModel.idPropietario,
    nombresPropietario: defaultModel.nombresPropietario,
    datosPropietario: defaultModel.datosPropietario
  };
};

const resetearDatosBasicos = (model: Model): Model => {
  return {
    ...model,
    contrataTransporte: defaultModel.contrataTransporte,
    numeroNochesIndividuales: defaultModel.numeroNochesIndividuales,
    valorFinalNocheIndividual: defaultModel.valorFinalNocheIndividual,
    tarifaNocheIndividual: defaultModel.tarifaNocheIndividual,
    tarifa: defaultModel.tarifa,
    valorFinalTarifa: defaultModel.valorFinalTarifa,
    ciudadOrigen: defaultModel.ciudadOrigen
  };
};

const resetearFechas = (model: Model): Model => {
  return {
    ...model,
    fechasProgramadas: defaultModel.fechasProgramadas,
    diasSeleccionados: defaultModel.diasSeleccionados,
  };
};

const resetearDatosTransporte = (model: Model): Model => {
  return {
    ...model,
    sectorTransporte: defaultModel.sectorTransporte,
    direccionTransporte: defaultModel.direccionTransporte,
    contrataIda: defaultModel.contrataIda,
    contrataVuelta: defaultModel.contrataVuelta,
  };
};

const resetearCalculoPrecioTransporte = (model: Model): Model => {
  return {
    ...model,
    tarifaIda: defaultModel.tarifaIda,
    valorFinalIda: defaultModel.valorFinalIda,
    tarifaVuelta: defaultModel.tarifaVuelta,
    valorFinalVuelta: defaultModel.valorFinalVuelta,
    tarifaIdaVuelta: defaultModel.tarifaIdaVuelta,
    valorFinalIdaVuelta: defaultModel.valorFinalIdaVuelta,
    usarTarifasPorTrayecto: defaultModel.usarTarifasPorTrayecto,
  };
};

const resetearResumen = (model: Model): Model => {
  return {
    ...model,
    descuento: defaultModel.descuento,
  };
};

const resetsPasos: Array<(model: Model) => Model> = [
  resetearSeleccionarMascota,
  resetearDatosBasicos,
  resetearFechas,
  resetearDatosTransporte,
  resetearCalculoPrecioTransporte,
  resetearResumen,
];

const IndicesPasos = {
  SeleccionarMascota: 0,
  DatosBasicos: 1,
  Fechas: 2,
  DatosTransporte: 3,
  CalculoPrecioTransporte: 4,
  Resumen: 5,
};

const resetearValoresPasosPosteriores = (
  model: Model,
  indicePaso: number
): Model => {
  let modelActualizado: Model = model;
  for (let i = indicePaso + 1; i < resetsPasos.length; i++) {
    modelActualizado = resetsPasos[i](modelActualizado);
  }
  return modelActualizado;
};
